// ─────────────────────────────────────────────────────────────────────────────
// MyProfileModal, the self-edit "My profile" form, ported 1:1 from the legacy
// profile-wiring.js renderEditor (CSS lifted verbatim into my-profile.css). Loads
// the signed-in user's own profile via fetchMyProfile (GET /api/me → the gated
// /api/users/:id/profile, which returns travel for self), and saves through
// saveMyProfile (PUT /api/me/profile).
//
// Fields: avatar (upload → data URL / remove), first + last name (recomposed into
// display_name on save), phone, read-only email, location, bio, a curated-catalog
// skills tag editor, visibility (friends/private), and the travel block (KTN,
// redress, airline + hotel loyalty rows). The legacy "Delete account" danger zone
// is intentionally omitted here, permanent account deletion must be user-driven.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchMyProfile,
  saveMyProfile,
  type LoyaltyProgram,
  type MyProfileSave,
} from '../../lib/contacts';
import './my-profile.css';

// Curated skills catalog, verbatim from profile-wiring.js SKILLS_CATALOG. Only an
// exact (case-insensitive) match from this list commits, so skills stay useful for
// finding collaborators rather than free-text noise.
const SKILLS_CATALOG: readonly string[] = [
  'Acoustic Guitar', 'Electric Guitar', 'Bass Guitar', 'Upright Bass',
  'Drums', 'Percussion', 'Vocals', 'Lead Vocals', 'BGVs',
  'Piano', 'Keys', 'Synth', 'Organ', 'Saxophone', 'Trumpet', 'Trombone',
  'Violin', 'Viola', 'Cello', 'Banjo', 'Mandolin', 'Pedal Steel',
  'Lap Steel', 'Harmonica', 'Accordion', 'Ukulele', 'DJ',
  'Producing', 'Mixing', 'Mastering', 'Recording Engineering',
  'Songwriting', 'Co-writing', 'Arranging', 'Composing',
  'Sound Design', 'Beat Making', 'Sample Programming',
  'Vocal Production', 'Vocal Tuning', 'Comping', 'Editing',
  'Tracking', 'Drum Programming', 'String Arrangement',
  'Pro Tools', 'Logic Pro', 'Ableton Live', 'Cubase', 'FL Studio',
  'Reason', 'Reaper', 'Studio One', 'GarageBand', 'Nuendo',
  'Maschine', 'Splice', 'Native Instruments', 'Serato', 'Rekordbox',
  'Photoshop', 'Lightroom', 'Illustrator', 'InDesign', 'Capture One',
  'Premiere Pro', 'After Effects', 'Final Cut Pro', 'DaVinci Resolve',
  'CapCut', 'Figma', 'Canva', 'Procreate',
  'FOH Engineer', 'Monitor Engineer', 'Lighting Design', 'Lighting Tech',
  'Stage Tech', 'Drum Tech', 'Guitar Tech', 'Bass Tech', 'Backline',
  'Tour Manager', 'Tour Accountant', 'Production Manager',
  'Stage Manager', 'Merch Manager',
  'A&R', 'Booking', 'Publicity', 'Marketing', 'Social Media',
  'Photography', 'Videography', 'Music Video Direction',
  'Graphic Design', 'Merch Design', 'Brand Strategy',
  'Tour Routing', 'Setlist Curation', 'Songwriter Pitching',
];

/* audit 2026-07-02, compress the avatar before storing it as base64 (ports
   profile-wiring.js compressImage :121-144: scale so the longest edge is
   <= 600px, re-encode JPEG at 0.85). Previously the raw file was stored
   uncompressed/uncapped, a camera photo is 5-12MB of base64, which blows up
   the profile-save payload. Rejects (instead of falling back to the raw file)
   so an unreadable image never sneaks the uncapped path back in. */
function compressAvatar(file: File, maxDim = 600, quality = 0.85): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = (e) => {
      const src = String((e.target as FileReader).result || '');
      const img = new Image();
      img.onerror = () => reject(new Error('decode failed'));
      img.onload = () => {
        try {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;
          if (!w || !h) { reject(new Error('decode failed')); return; }
          const scale = Math.min(1, maxDim / Math.max(w, h));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('canvas unavailable')); return; }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (err) {
          reject(err instanceof Error ? err : new Error('compress failed'));
        }
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });
}

function initials(s: string): string {
  const parts = String(s || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface MyProfileModalProps {
  readonly onClose: () => void;
  /** Called after a successful save (e.g. to refresh surfaces). */
  readonly onSaved?: () => void;
}

export function MyProfileModal({ onClose, onSaved }: MyProfileModalProps): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'err' | 'ok'; text: string } | null>(null);

  const [avatarUrl, setAvatarUrl] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [visibility, setVisibility] = useState('friends');
  const [ktn, setKtn] = useState('');
  const [redress, setRedress] = useState('');
  const [airlines, setAirlines] = useState<LoyaltyProgram[]>([]);
  const [hotels, setHotels] = useState<LoyaltyProgram[]>([]);

  const [skillInput, setSkillInput] = useState('');
  const [skillErr, setSkillErr] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let live = true;
    fetchMyProfile().then((p) => {
      if (!live || !p) { if (live) setLoading(false); return; }
      const dn = (p.display_name || '').trim().replace(/\s+/g, ' ');
      const sp = dn.indexOf(' ');
      setFirstName(sp === -1 ? dn : dn.slice(0, sp));
      setLastName(sp === -1 ? '' : dn.slice(sp + 1));
      setAvatarUrl(p.avatar_url || '');
      setPhone(p.phone || '');
      setEmail(p.email || '');
      setLocation(p.location || '');
      setBio(p.bio || '');
      setSkills(p.skills.slice());
      setVisibility(p.profile_visibility || 'friends');
      const t = p.travel || {};
      setKtn(t.known_traveler_number || '');
      setRedress(t.redress_number || '');
      setAirlines((t.airline_loyalty || []).map((r) => ({ program: r.program || '', number: r.number || '', tier: r.tier })));
      setHotels((t.hotel_loyalty || []).map((r) => ({ program: r.program || '', number: r.number || '', tier: r.tier })));
      setLoading(false);
    }).catch(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const suggestions = useMemo(() => {
    const q = skillInput.trim().toLowerCase();
    if (!q) return [];
    const taken = new Set(skills.map((s) => s.toLowerCase()));
    return SKILLS_CATALOG.filter((s) => !taken.has(s.toLowerCase()) && s.toLowerCase().indexOf(q) !== -1).slice(0, 8);
  }, [skillInput, skills]);

  function addSkill(s: string): void {
    const canon = SKILLS_CATALOG.find((c) => c.toLowerCase() === s.toLowerCase());
    if (!canon) { setSkillErr('Pick a skill from the suggestions list.'); return; }
    setSkillErr('');
    if (skills.length >= 30) { setSkillErr('Max 30 skills.'); return; }
    if (!skills.some((x) => x.toLowerCase() === canon.toLowerCase())) setSkills((arr) => [...arr, canon]);
    setSkillInput('');
  }
  function removeSkill(s: string): void { setSkills((arr) => arr.filter((x) => x !== s)); }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    // audit 2026-07-02, 600px canvas compress before storing (profile-wiring.js
    // pattern); the raw FileReader path stored uncompressed multi-MB base64.
    void compressAvatar(f)
      .then((dataUrl) => setAvatarUrl(dataUrl))
      .catch(() => setMsg({ kind: 'err', text: 'Could not read that image, try a different one.' }));
  }

  function updateRow(kind: 'air' | 'hotel', i: number, field: 'program' | 'number', val: string): void {
    const set = kind === 'air' ? setAirlines : setHotels;
    set((arr) => arr.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  }
  function removeRow(kind: 'air' | 'hotel', i: number): void {
    const set = kind === 'air' ? setAirlines : setHotels;
    set((arr) => arr.filter((_, idx) => idx !== i));
  }

  async function onSave(): Promise<void> {
    setSaving(true);
    setMsg(null);
    const cleanAir = airlines.filter((r) => (r.program || '').trim() || (r.number || '').trim());
    const cleanHotel = hotels.filter((r) => (r.program || '').trim() || (r.number || '').trim());
    const payload: MyProfileSave = {
      display_name: (firstName.trim() + ' ' + lastName.trim()).trim(),
      phone: phone.trim(),
      bio: bio.trim(),
      avatar_url: avatarUrl,
      location: location.trim(),
      skills,
      profile_visibility: visibility,
      travel_known_traveler_number: ktn.trim(),
      travel_redress_number: redress.trim(),
      travel_airline_loyalty: cleanAir,
      travel_hotel_loyalty: cleanHotel,
    };
    const ok = await saveMyProfile(payload);
    setSaving(false);
    if (ok) { setMsg({ kind: 'ok', text: 'Profile saved.' }); onSaved?.(); setTimeout(onClose, 500); }
    else setMsg({ kind: 'err', text: 'Could not save, try again.' });
  }

  const loyaltyBlock = (kind: 'air' | 'hotel', rows: LoyaltyProgram[]): JSX.Element => (
    <div className="profile-edit__loyalty">
      {rows.length === 0 && <p className="profile-edit__loyalty-empty">None added.</p>}
      {rows.map((r, i) => (
        <div className="profile-edit__loyalty-row" key={i}>
          <input className="profile-edit__input" placeholder={kind === 'air' ? 'Airline (e.g. Delta)' : 'Hotel (e.g. Marriott)'} value={r.program || ''} onChange={(e) => updateRow(kind, i, 'program', e.target.value)} />
          <input className="profile-edit__input" placeholder="Member #" value={r.number || ''} onChange={(e) => updateRow(kind, i, 'number', e.target.value)} />
          <button type="button" className="profile-edit__loyalty-x" aria-label="Remove" onClick={() => removeRow(kind, i)}>×</button>
        </div>
      ))}
    </div>
  );

  // Portal to <body> so `position:fixed` is relative to the viewport, not the
  // nav header (which has a transform/contain that would trap the fixed overlay
  // to an ~80px-tall containing block, the "blank sliver" bug). Mirrors the
  // working RosterProfileModal, which already portals for the same reason.
  return createPortal(
    <div className="mp-overlay">
      <div className="mp-scrim" onClick={onClose} />
      <div className="mp-panel" role="dialog" aria-label="My profile" data-screen-label="My profile">
        <div className="mp-head">
          <h2 className="mp-title">My profile</h2>
          <button className="mp-close" type="button" aria-label="Close" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
        </div>

        {loading ? (
          <p className="mp-msg">Loading…</p>
        ) : (
          <>
            {msg && <p className={'mp-msg mp-msg--' + msg.kind}>{msg.text}</p>}
            <div className="profile-edit">
              {/* Avatar */}
              <div className="profile-edit__avatar-row">
                <button type="button" className="profile-edit__avatar" aria-label="Change profile photo" onClick={() => fileRef.current?.click()}>
                  {avatarUrl ? <img src={avatarUrl} alt="" /> : <span className="profile-edit__avatar-initials">{initials(firstName + ' ' + lastName || email)}</span>}
                  <span className="profile-edit__avatar-overlay">Change</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />
                <div className="profile-edit__avatar-meta">
                  <p className="profile-edit__avatar-hint">JPG or PNG, square works best.</p>
                  {avatarUrl && <button type="button" className="btn-link" onClick={() => setAvatarUrl('')}>Remove photo</button>}
                </div>
              </div>

              {/* Name */}
              <div className="profile-edit__name-row">
                <div className="profile-edit__name-col">
                  <label className="profile-edit__label" htmlFor="mpFirst">First name</label>
                  <input id="mpFirst" className="profile-edit__input" type="text" value={firstName} placeholder="First" onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div className="profile-edit__name-col">
                  <label className="profile-edit__label" htmlFor="mpLast">Last name</label>
                  <input id="mpLast" className="profile-edit__input" type="text" value={lastName} placeholder="Last" onChange={(e) => setLastName(e.target.value)} />
                </div>
              </div>

              <label className="profile-edit__label">Phone</label>
              <input className="profile-edit__input" type="tel" value={phone} placeholder="+1 615 555 1234" onChange={(e) => setPhone(e.target.value)} />

              <label className="profile-edit__label">Email <span className="profile-edit__sub">(read-only, sign-in identifier)</span></label>
              <input className="profile-edit__input" type="email" value={email} disabled />

              <label className="profile-edit__label">Location <span className="profile-edit__sub">(city, state)</span></label>
              <input className="profile-edit__input" type="text" value={location} placeholder="Nashville, TN" onChange={(e) => setLocation(e.target.value)} />

              <label className="profile-edit__label">Bio</label>
              <textarea className="profile-edit__input profile-edit__textarea" rows={3} maxLength={1000} value={bio} placeholder="A short blurb, what you play, what you're looking for, anything else." onChange={(e) => setBio(e.target.value)} />

              {/* Skills */}
              <label className="profile-edit__label">Skills <span className="profile-edit__sub">(tags, Ableton, FOH, upright bass, tour management…)</span></label>
              <div className="profile-edit__skills">
                {skills.length === 0 && <span className="profile-edit__skills-empty">No skills yet, add some below.</span>}
                {skills.map((s) => (
                  <span className="profile-edit__skill-chip" key={s}>{s}<button type="button" className="profile-edit__skill-x" aria-label={'Remove ' + s} onClick={() => removeSkill(s)}>×</button></span>
                ))}
              </div>
              <div className="profile-edit__skill-typeahead">
                <input
                  className="profile-edit__input profile-edit__skill-input"
                  type="text"
                  value={skillInput}
                  placeholder="Start typing, pick from suggestions"
                  autoComplete="off"
                  onChange={(e) => { setSkillInput(e.target.value); setSkillErr(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (suggestions.length) addSkill(suggestions[0]); else setSkillErr('Pick a skill from the suggestions list.'); } }}
                />
                {suggestions.length > 0 && (
                  <ul className="profile-edit__skill-suggestions">
                    {suggestions.map((s) => (
                      <li className="profile-edit__skill-suggestion" key={s} onClick={() => addSkill(s)}>{s}</li>
                    ))}
                  </ul>
                )}
                {skillErr && <p className="profile-edit__skill-error">{skillErr}</p>}
              </div>

              {/* Visibility */}
              <label className="profile-edit__label">Visibility</label>
              <div className="profile-edit__visibility">
                <label><input type="radio" name="mpVis" value="friends" checked={visibility !== 'private'} onChange={() => setVisibility('friends')} /> <span><strong>Friends</strong>, your bands and their friend bands can see this profile</span></label>
                <label><input type="radio" name="mpVis" value="private" checked={visibility === 'private'} onChange={() => setVisibility('private')} /> <span><strong>Private</strong>, only your current bandmates can see this profile</span></label>
              </div>

              {/* Travel */}
              <h4 className="profile-edit__section-head">Travel info</h4>
              <p className="profile-edit__section-sub">Helps your tour manager and booking agent book flights and hotels. Hidden from band members, crew, and FOH, only artist, manager, tour manager, and booking agent see this.</p>

              <label className="profile-edit__label">Known Traveler Number (KTN) <span className="profile-edit__sub">(used for both TSA PreCheck and Global Entry)</span></label>
              <input className="profile-edit__input" type="text" inputMode="numeric" maxLength={20} value={ktn} placeholder="9-digit number" onChange={(e) => setKtn(e.target.value)} />

              <label className="profile-edit__label">Redress Number <span className="profile-edit__sub">(optional, DHS-issued, only if you have one)</span></label>
              <input className="profile-edit__input" type="text" inputMode="numeric" maxLength={20} value={redress} onChange={(e) => setRedress(e.target.value)} />

              <label className="profile-edit__label">Airline loyalty</label>
              {loyaltyBlock('air', airlines)}
              <button type="button" className="btn-pill btn-pill--ghost btn-sm" onClick={() => setAirlines((a) => [...a, { program: '', number: '' }])}>+ Add airline</button>

              <label className="profile-edit__label">Hotel loyalty</label>
              {loyaltyBlock('hotel', hotels)}
              <button type="button" className="btn-pill btn-pill--ghost btn-sm" onClick={() => setHotels((h) => [...h, { program: '', number: '' }])}>+ Add hotel</button>

              {/* Actions */}
              <div className="profile-edit__actions">
                <button type="button" className="btn-pill btn-pill--ghost" onClick={onClose}>Cancel</button>
                <button type="button" className="btn-pill btn-pill--dark" disabled={saving} onClick={() => void onSave()}>{saving ? 'Saving…' : 'Save profile'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
